import { useState, useMemo, useCallback } from 'react'
import { AdminLayout } from '~/components/AdminLayout'
import { useUsers, useCreateUser, useUpdateUser, type User, type UserRole } from '~/api/users'
import { UserModal, type UserFormData } from '~/components/admin/UserModal'
import 'remixicon/fonts/remixicon.css'
import styles from './users.module.css'

/**
 * User role filter options
 */
const ROLE_FILTERS = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'operations_manager', label: 'Operations Manager' },
] as const

/**
 * Get the CSS class for a role badge
 */
function getRoleClass(role: UserRole): string {
  switch (role) {
    case 'admin':
      return styles.roleAdmin
    case 'customer':
      return styles.roleCustomer
    case 'driver':
      return styles.roleDriver
    case 'operations_manager':
      return styles.roleOperationsManager
    default:
      return ''
  }
}

/**
 * Format role for display (replace underscores with spaces)
 */
function formatRole(role: string): string {
  return role.replace(/_/g, ' ')
}

/**
 * Format date to readable string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Get full name from user
 */
function getFullName(user: User): string {
  return `${user.firstName} ${user.lastName}`.trim()
}

/**
 * Admin Users Page - Data table showing all users with filtering and stats
 * 
 * Users are read-only. User registration is handled through the normal signup flow.
 */
export function AdminUsersPage() {
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  // Fetch all users
  const { data: users, isLoading, isError, error } = useUsers()
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()

  // Calculate stats from all users
  const stats = useMemo(() => {
    if (!users) return { total: 0, admins: 0, customers: 0, drivers: 0 }
    return {
      total: users.length,
      admins: users.filter((u) => u.roles.includes('admin')).length,
      customers: users.filter((u) => u.roles.includes('customer')).length,
      drivers: users.filter((u) => u.roles.includes('driver')).length,
    }
  }, [users])

  // Filter users by role
  const roleFilteredUsers = useMemo(() => {
    if (!users) return []
    if (!roleFilter) return users
    return users.filter((user) => user.roles.includes(roleFilter as UserRole))
  }, [users, roleFilter])

  // Filter users by search query (client-side)
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return roleFilteredUsers
    const query = searchQuery.toLowerCase()
    return roleFilteredUsers.filter(
      (user) =>
        getFullName(user).toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
    )
  }, [roleFilteredUsers, searchQuery])

  // Handle role filter change
  const handleRoleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value)
  }, [])

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  // Modal handlers
  const handleCreateClick = useCallback(() => {
    setEditingUser(null)
    setModalOpen(true)
  }, [])

  const handleEditClick = useCallback((user: User) => {
    setEditingUser(user)
    setModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setModalOpen(false)
    setEditingUser(null)
  }, [])

  const handleSave = useCallback(async (data: UserFormData) => {
    if (editingUser) {
      // Update existing user
      const addRoles = data.roles.filter(r => !editingUser.roles.includes(r as UserRole))
      const removeRoles = editingUser.roles.filter(r => !data.roles.includes(r))
      await updateUser.mutateAsync({
        userId: editingUser.id,
        email: data.email !== editingUser.email ? data.email : undefined,
        firstName: data.firstName !== editingUser.firstName ? data.firstName : undefined,
        lastName: data.lastName !== editingUser.lastName ? data.lastName : undefined,
        enabled: data.enabled !== editingUser.enabled ? data.enabled : undefined,
        addRoles: addRoles.length > 0 ? addRoles : undefined,
        removeRoles: removeRoles.length > 0 ? removeRoles : undefined,
      })
    } else {
      // Create new user
      await createUser.mutateAsync(data)
    }
    handleModalClose()
  }, [editingUser, createUser, updateUser, handleModalClose])

  if (isLoading) {
    return (
      <AdminLayout title="USERS">
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading users...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError) {
    return (
      <AdminLayout title="USERS">
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>
            Error: {error?.message || 'Failed to load users'}
          </span>
        </div>
      </AdminLayout>
    )
  }

  const hasUsers = filteredUsers.length > 0
  const showEmptySearch = users && users.length > 0 && filteredUsers.length === 0

  return (
    <AdminLayout title="USERS">
      <div className={styles.usersPage}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <p className={styles.pageDescription}>
            Manage users registered in the system. Create new users or edit existing ones.
          </p>
          <button className={styles.createButton} onClick={handleCreateClick}>
            + CREATE USER
          </button>
        </div>

        {/* Stats Summary */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total Users</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Admins</span>
            <span className={`${styles.statValue} ${styles.statValueDanger}`}>
              {stats.admins}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Customers</span>
            <span className={`${styles.statValue} ${styles.statValueSuccess}`}>
              {stats.customers}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Drivers</span>
            <span className={`${styles.statValue} ${styles.statValueAccent}`}>
              {stats.drivers}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className={styles.controls}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, username, or email..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search users"
          />
          <select
            className={styles.filterSelect}
            value={roleFilter}
            onChange={handleRoleChange}
            aria-label="Filter by role"
          >
            {ROLE_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          {users && (
            <span className={styles.resultsCount}>
              {filteredUsers.length} of {users.length} users
            </span>
          )}
        </div>

        {/* Table or Empty State */}
        {!hasUsers ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyText}>
              {showEmptySearch ? 'No users found matching your search' : 'No users yet'}
            </span>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>NAME</th>
                  <th className={styles.th}>USERNAME</th>
                  <th className={styles.th}>EMAIL</th>
                  <th className={styles.th}>ROLES</th>
                  <th className={styles.th}>STATUS</th>
                  <th className={styles.th}>JOINED</th>
                  <th className={styles.th}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={styles.tr}>
                    <td className={styles.td}>
                      <span className={styles.userName}>{getFullName(user)}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.username}>{user.username}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.email}>{user.email}</span>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.rolesContainer}>
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className={`${styles.roleBadge} ${getRoleClass(role)}`}
                          >
                            {formatRole(role)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <span
                        className={`${styles.statusBadge} ${
                          user.enabled ? styles.statusEnabled : styles.statusDisabled
                        }`}
                      >
                        {user.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.date}>{formatDate(user.createdAt)}</span>
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditClick(user)}
                        aria-label={`Edit ${user.username}`}
                      >
                        <i className="ri-edit-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* User Modal */}
        <UserModal
          user={editingUser}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSave={handleSave}
          isSaving={createUser.isPending || updateUser.isPending}
        />
      </div>
    </AdminLayout>
  )
}
